import { Copy, ExternalLink } from 'lucide-react-native';

import { useSourcePublication } from '../../hooks/useSourcePublication';
import { REPLY_DRAFT_COPY } from '../copy';
import type { SourceRef } from '../shared/SourceViewer';
import { openSource } from '../shared/openSource';

import { PublicationContent } from '@/components/publication/PublicationContent';
import { getOpenPublicationLabel } from '@/components/publication/publicationView';
import { Button, ButtonIcon, ButtonText } from '@/components/ui/button';
import { Center } from '@/components/ui/center';
import { HStack } from '@/components/ui/hstack';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import useClipboard from '@/hooks/useClipboard';

/**
 * A source's details and summary in the reply draft, drawn by the same
 * component as a reference in a message so a work reads alike in both.
 * Copying the citation and opening the page sit at its foot, where the expert
 * lands after reading, since the sheet's header holds the way back.
 *
 * A record that fails to load still leaves the page one tap away, so the
 * evidence is never out of reach because the catalog read failed.
 */
export function SourceDetail({ source }: { source: SourceRef }) {
  const query = useSourcePublication(source.publicationId);

  const { copyToClipboard } = useClipboard();

  if (query.isPending) {
    return (
      <Center className="py-10" accessibilityLiveRegion="polite">
        <Spinner size="large" accessibilityLabel={REPLY_DRAFT_COPY.loading} />
      </Center>
    );
  }

  if (query.isError) {
    return (
      <VStack className="items-center gap-3 py-10" testID="reply-draft-source-failed">
        <Text className="text-typography-900" accessibilityLiveRegion="polite">
          {REPLY_DRAFT_COPY.sourceLoadFailed}
        </Text>
        <HStack className="gap-3">
          <Button
            variant="outline"
            action="secondary"
            className="min-h-12"
            onPress={() => {
              query.refetch().catch(() => undefined);
            }}>
            <ButtonText className="text-secondary-700">{REPLY_DRAFT_COPY.retry}</ButtonText>
          </Button>
          <Button
            variant="outline"
            action="secondary"
            className="min-h-12"
            onPress={() => {
              openSource(source.url);
            }}>
            <ButtonText className="text-secondary-700">
              {REPLY_DRAFT_COPY.openLinkInstead}
            </ButtonText>
          </Button>
        </HStack>
      </VStack>
    );
  }

  const publication = query.data;

  return (
    <VStack className="gap-6" testID="reply-draft-source">
      <PublicationContent
        title={publication.title?.trim().replace(/\.$/, '') || source.title}
        publication={publication}
      />
      <HStack className="flex-wrap gap-3">
        <Button
          variant="outline"
          action="secondary"
          className="min-h-12"
          onPress={() => {
            copyToClipboard(publication.citation, undefined, REPLY_DRAFT_COPY.citationCopied).catch(
              () => undefined
            );
          }}
          testID="reply-draft-source-copy">
          <ButtonIcon as={Copy} className="text-secondary-700" />
          <ButtonText className="text-secondary-700">{REPLY_DRAFT_COPY.copyCitation}</ButtonText>
        </Button>
        <Button
          variant="outline"
          action="secondary"
          className="min-h-12"
          onPress={() => {
            openSource(publication.url);
          }}
          testID="reply-draft-source-open">
          <ButtonIcon as={ExternalLink} className="text-secondary-700" />
          <ButtonText className="text-secondary-700">
            {getOpenPublicationLabel(publication.kind)}
          </ButtonText>
        </Button>
      </HStack>
    </VStack>
  );
}
